import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../../../components/axiosInstance";
import { API_URLS } from "../../../../apiUrl";
import { useNavigate } from "react-router-dom";

interface Borough { id: number; borough_name: any; borough_primary_id: any; }
interface Ward { id: number; name: string; }
interface RoleType { primary_role_id: number; role_name: string; status: number; }

interface FormData {
  login_user_name: string;
  admin_name: string;
  municipality_name: string;
  password: string;
  email: string;
  role_id: string;
  ward_id: string;
  municipality_id: any;
}


const Addcashier = () => {
  const MunicipalityID = localStorage.getItem("municipalityId");
  const municipalityName = localStorage.getItem("municipalityName") ?? "";
  
  const EMPTY_FORM: FormData = {
  login_user_name: "", admin_name: "", municipality_name: municipalityName,
  password: "", email: "", role_id: "", ward_id: "", municipality_id: MunicipalityID,
};

  const [boroughs, setBoroughs] = useState<Borough[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const [roleTypes, setRoleTypes] = useState<RoleType[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [selectedBorough, setSelectedBorough] = useState<string>("");
  const [selectedWard, setSelectedWard] = useState<string>("");
  const [loadingBorough, setLoadingBorough] = useState(false);
  const [loadingWard, setLoadingWard] = useState(false);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = React.useRef(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);



  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // ✅ Email is valid only if provided AND matches regex, or if empty
  const emailValid = !form.email.trim() || emailRegex.test(form.email);
  
  const passwordChecks = {
    length: form.password.length >= 6,
    uppercase: /[A-Z]/.test(form.password),
    number: /\d/.test(form.password),
  };
  const passwordValid = Object.values(passwordChecks).every(Boolean);

  const selectedRoleName = roleTypes.find(r => String(r.primary_role_id) === selectedRole)?.role_name ?? "";
  const isFieldCollector = selectedRoleName === "collector";

  const showForm =
    selectedRole !== "" &&
    (!isFieldCollector || (selectedBorough !== "" && selectedWard !== ""));

  const nav = useNavigate();

  // Load role types and auto-select cashier
  useEffect(() => {
    axiosInstance.get(API_URLS.roleType)
      .then(res => {
        const data: RoleType[] = res.data?.data || [];
        setRoleTypes(data);
        const cashier = data.find(r => r.role_name.toLowerCase() === "cashier");
        if (cashier) {
          const id = String(cashier.primary_role_id);
          setSelectedRole(id);
          setForm(prev => ({ ...prev, role_id: id }));
        }
      })
      .catch((err: any) => {
        const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to load role types";
        toast.error(msg, { position: "top-right", autoClose: 3000 });
      });
  }, []);

  // Load boroughs when field collector selected
  useEffect(() => {
    if (!isFieldCollector) {
      setBoroughs([]);
      setSelectedBorough("");
      setSelectedWard("");
      setWards([]);
      return;
    }
    setLoadingBorough(true);
    axiosInstance.post(API_URLS.GetMunicipalityBorough, {
      municipalityTbl_id: MunicipalityID, page: 1, limit: 500, surveyer_id: 0,
    })
      .then(res => setBoroughs(res.data?.data?.data || []))
      .catch((err: any) => {
        const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to load boroughs";
        toast.error(msg, { position: "top-right", autoClose: 3000 });
      })
      .finally(() => setLoadingBorough(false));
  }, [isFieldCollector]);

  // Load wards when borough selected
  useEffect(() => {
    if (!selectedBorough) return;
    setSelectedWard("");
    setWards([]);
    setLoadingWard(true);
    axiosInstance.post(API_URLS.GetBoroughWiseWard, {
      municipalityTbl_id: MunicipalityID, borough_primary_id: selectedBorough,
      page: 1, limit: 50, surveyer_id: 0,
    })
      .then(res => {
        const wardList = res.data?.data?.data?.map((w: any) => ({
          id: w.ward_primary_id, name: w.ward_no,
        })) || [];
        setWards(wardList);
      })
      .catch((err: any) => {
        const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to load wards";
        toast.error(msg, { position: "top-right", autoClose: 3000 });
      })
      .finally(() => setLoadingWard(false));
  }, [selectedBorough]);

  const handleRoleChange = (roleId: string) => {
    setSelectedRole(roleId);
    setSelectedBorough("");
    setSelectedWard("");
    setWards([]);
    setBoroughs([]);
    setForm({ ...EMPTY_FORM, role_id: roleId });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (e.target.name === "email") setEmailTouched(true);
    if (e.target.name === "password") setPasswordTouched(true);
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    // ✅ Required fields (email is NOT required anymore)
    const requiredFields: { key: keyof FormData; label: string }[] = [
      { key: "admin_name", label: "Full Name" },
      { key: "login_user_name", label: "Username" },
      { key: "password", label: "Password" },
      // Email removed from required fields
    ];
    
    for (const { key, label } of requiredFields) {
      if (!String(form[key] ?? "").trim()) {
        toast.warn(`Please fill in ${label}`, { position: "top-right", autoClose: 3000 });
        return;
      }
    }
    
    // ✅ Email validation only if email is provided
    if (form.email.trim() && !emailValid) {
      setEmailTouched(true);
      toast.warn("Please enter a valid email address", { position: "top-right", autoClose: 3000 });
      return;
    }
    
    if (!passwordValid) {
      setPasswordTouched(true);
      toast.warn("Please meet all password requirements", { position: "top-right", autoClose: 3000 });
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const payload: FormData = {
        login_user_name: form.login_user_name,
        admin_name: form.admin_name,
        municipality_name: form.municipality_name,
        password: form.password,
        email: form.email || "", // Send empty string if no email
        role_id: selectedRole,
        ward_id: selectedWard || "",
        municipality_id: MunicipalityID,
      };

      console.log(MunicipalityID, "<-----Municipality ID")

      await axiosInstance.post(API_URLS.collectorcreate, payload);

      // Reset everything
      setSelectedRole("");
      setSelectedBorough("");
      setSelectedWard("");
      setBoroughs([]);
      setWards([]);
      setEmailTouched(false);
      setPasswordTouched(false);
      setShowPassword(false);
      setForm(EMPTY_FORM);

      toast.success(`${isFieldCollector ? "Field" : "Counter"} Collector added successfully!`, {
        position: "top-right",
        autoClose: 1000,
        onClose: () => nav('/dashboard/view-cashier'),
      });

    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Something went wrong.";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
    } finally {
      setTimeout(() => { isSubmittingRef.current = false; setIsSubmitting(false); }, 3000);
    }
  };

  const fields: { name: keyof FormData; max: number; min: number; label: string; type?: string; placeholder: string; optional?: boolean }[] = [
    { name: "admin_name",      max: 100, min: 2, label: "Full Name", placeholder: "Rahul Kumar" },
    { name: "login_user_name", max: 100, min: 2, label: "Username",  placeholder: "rahul_tmr" },
    { name: "email",           max: 100, min: 2, label: "Email",     type: "email", placeholder: "rahul@gmail.com", optional: true },
    { name: "password",        max: 20,  min: 6, label: "Password",  type: "password", placeholder: "Min 6 chars, 1 uppercase, 1 number" },
  ];

  return (
    <div className="bg-gray-100 min-h-screen p-6">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Add Cashier</h1>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">

          {/* Role Type */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Role Type</label>
            <div className="relative">
              <select
                value={selectedRole}
                disabled
                className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm appearance-none bg-gray-100 text-gray-400 cursor-not-allowed pr-8"
              >
                <option value="">Select Collector Type</option>
                {roleTypes.map(r => (
                  <option key={r.primary_role_id} value={String(r.primary_role_id)}>
                    {r.role_name}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
            </div>
          </div>
        </div>

        {isFieldCollector && selectedBorough === "" && (
          <p className="text-xs text-orange-500 mt-3">👆 Select a Borough to continue</p>
        )}
        {isFieldCollector && selectedBorough !== "" && selectedWard === "" && (
          <p className="text-xs text-orange-500 mt-3">👆 Select a Ward to unlock the form</p>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              isFieldCollector ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
            }`}>
              {selectedRoleName}
            </span>
            {isFieldCollector && selectedWard && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                Ward: {wards.find(w => String(w.id) === selectedWard)?.name || selectedWard}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {fields.map(f => (
              <div key={f.name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {f.label} 
                  {!f.optional && <span className="text-red-500 ml-1">*</span>}
                  {f.optional && <span className="text-gray-400 text-xs ml-1">(Optional)</span>}
                </label>
                <div className="relative">
                  <input
                    type={f.name === "password" ? (showPassword ? "text" : "password") : (f.type || "text")}
                    name={f.name}
                    value={form[f.name]}
                    onChange={handleChange}
                    onBlur={() => {
                      if (f.name === "email") setEmailTouched(true);
                      if (f.name === "password") setPasswordTouched(true);
                    }}
                    placeholder={f.placeholder}
                    autoComplete={f.name === "password" ? "new-password" : "off"}
                    className={`w-full border rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                      f.name === "email" && emailTouched && form.email
                        ? emailValid ? "border-green-400 focus:ring-green-400" : "border-red-400 focus:ring-red-400"
                        : f.name === "password" && passwordTouched && form.password
                        ? passwordValid ? "border-green-400 focus:ring-green-400" : "border-red-400 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-400"
                    }`}
                    maxLength={f.max || undefined}
                    minLength={f.min || undefined}
                  />
                  {f.name === "password" && (
                    <button type="button" onClick={() => setShowPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword
                        ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" /></svg>
                        : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      }
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {emailTouched && form.email && !emailValid && (
            <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Please enter a valid email address
            </p>
          )}

          {/* Password checker */}
          {passwordTouched && form.password && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs font-medium text-gray-600 mb-2">Password requirements:</p>
              <div className="flex flex-col gap-1">
                {[
                  { key: "length", label: "At least 6 characters" },
                  { key: "uppercase", label: "At least 1 uppercase letter (A-Z)" },
                  { key: "number", label: "At least 1 number (0-9)" },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                      passwordChecks[key as keyof typeof passwordChecks] ? "bg-green-500" : "bg-gray-300"
                    }`}>
                      {passwordChecks[key as keyof typeof passwordChecks] && (
                        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
                          <path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </span>
                    <span className={`text-xs ${passwordChecks[key as keyof typeof passwordChecks] ? "text-green-600" : "text-gray-500"}`}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-8">
            <button type="button" onClick={handleSubmit} disabled={isSubmitting}
              className="flex-1 py-3 rounded-lg font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-400 bg-blue-600 hover:bg-blue-700">
              {isSubmitting ? "Saving..." : `Add ${selectedRoleName}`}
            </button>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default Addcashier;